"use client"

import { useEffect, useRef } from "react"
import {
  ExternalLink,
  Bell,
  CheckSquare,
  Car,
  Play,
  CheckCircle2,
  XCircle,
  User as UserIcon,
  Copy as CopyIcon,
  Repeat,
} from "lucide-react"

// Reusable right-click / three-dots menu for a job card (issue #36).
// Mounted at the schedule page's root so the floating menu can position
// relative to the cursor. Opens via onContextMenu on any job chip; the
// parent passes the job and the clientX/clientY from the event.
//
// Keeps logic out of the chip — the chip only tells the parent "this
// job was right-clicked at (x,y)" and the parent decides what the menu
// does. The parent-passed handlers do the heavy lifting (navigate,
// open task modal, status transition API, cancel).

export default function JobContextMenu({
  state,            // { job, x, y } | null
  onClose,
  onOpenJob,        // (job) => void
  onSetReminders,   // (job) => void — usually navigate to settings
  onCreateTask,     // (job) => void — opens CreateTaskModal
  onDuplicate,      // (job) => void — opens DuplicateJobModal (issue #38)
  onConvertRecurring, // (job) => void — opens ConvertToRecurringModal (#39)
  onSetStatus,      // (job, status) => Promise<void>
  onCancelJob,      // (job) => Promise<void>
}) {
  const ref = useRef(null)

  // Close on outside click / Esc / scroll — any interaction outside
  // the menu dismisses it so the user never gets a sticky overlay.
  useEffect(() => {
    if (!state) return
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose?.()
    }
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.()
    }
    document.addEventListener("mousedown", onDocClick)
    document.addEventListener("keydown", onKey)
    // Scroll closes too — the menu is positioned absolutely at a
    // viewport coord that becomes wrong once anything shifts.
    window.addEventListener("scroll", onClose, true)
    return () => {
      document.removeEventListener("mousedown", onDocClick)
      document.removeEventListener("keydown", onKey)
      window.removeEventListener("scroll", onClose, true)
    }
  }, [state, onClose])

  if (!state) return null

  const { job, x, y } = state

  // Keep the menu on screen when the click is near the right/bottom edge.
  // Rough measurements — a bit of overshoot is fine, we just want to
  // avoid obvious clipping on narrow laptop screens.
  const menuWidth = 240
  const menuHeight = 420
  const vw = typeof window !== "undefined" ? window.innerWidth : 1920
  const vh = typeof window !== "undefined" ? window.innerHeight : 1080
  const left = Math.min(x, vw - menuWidth - 12)
  const top = Math.min(y, vh - menuHeight - 12)

  const status = String(job?.status || "").toLowerCase()
  const canCancel = !["cancelled", "canceled", "completed", "paid"].includes(status)
  const canEnRoute = !["en_route", "en route", "in-progress", "in_progress", "completed", "paid", "cancelled"].includes(status)
  const canStart = ["en_route", "en route", "confirmed", "scheduled", "pending"].includes(status) && !["in-progress", "in_progress", "completed", "paid", "cancelled"].includes(status)
  const canComplete = !["completed", "paid", "cancelled"].includes(status)

  const customerName =
    `${job?.customers?.first_name || ""} ${job?.customers?.last_name || ""}`.trim() ||
    job?.customer_name ||
    `Job #${job?.id || ""}`

  const Item = ({ icon: Icon, label, onClick, danger, disabled }) => (
    <button
      type="button"
      onClick={() => {
        if (disabled) return
        onClose?.()
        onClick?.()
      }}
      disabled={disabled}
      className={`w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-left transition rounded-md
        ${disabled
          ? "text-[var(--sf-ink-3,#94a3b8)] cursor-not-allowed opacity-50"
          : danger
            ? "text-red-600 hover:bg-red-50"
            : "text-[var(--sf-ink,#0f172a)] hover:bg-[var(--sf-panel-soft,#f8fafc)]"
        }`}
    >
      <Icon className="w-4 h-4 shrink-0" />
      <span className="truncate">{label}</span>
    </button>
  )

  return (
    <div
      ref={ref}
      className="fixed z-[1000] bg-white rounded-lg shadow-xl border border-[var(--sf-border-light,#e2e8f0)] p-1.5"
      style={{ top, left, width: menuWidth }}
      role="menu"
    >
      <div className="px-3 pt-1.5 pb-2 border-b border-[var(--sf-border-light,#e2e8f0)] mb-1">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-[var(--sf-ink-3,#94a3b8)] uppercase tracking-wider">
          <UserIcon className="w-3 h-3" />
          Job
        </div>
        <div className="text-[13px] font-semibold text-[var(--sf-ink,#0f172a)] truncate mt-0.5">
          {customerName}
        </div>
        <div className="text-[11px] text-[var(--sf-ink-3,#94a3b8)] truncate">
          {job?.service_name || "Service"}
        </div>
      </div>

      <Item icon={ExternalLink} label="Open job" onClick={() => onOpenJob?.(job)} />
      <Item icon={Bell} label="Set reminder schedule" onClick={() => onSetReminders?.(job)} />
      <Item icon={CheckSquare} label="Create task" onClick={() => onCreateTask?.(job)} />
      <Item icon={CopyIcon} label="Duplicate job" onClick={() => onDuplicate?.(job)} />
      <Item icon={Repeat} label="Convert to recurring" onClick={() => onConvertRecurring?.(job)} />

      <div className="my-1 border-t border-[var(--sf-border-light,#e2e8f0)]" />

      <Item
        icon={Car}
        label="Mark en route"
        onClick={() => onSetStatus?.(job, "en_route")}
        disabled={!canEnRoute}
      />
      <Item
        icon={Play}
        label="Mark started"
        onClick={() => onSetStatus?.(job, "in-progress")}
        disabled={!canStart}
      />
      <Item
        icon={CheckCircle2}
        label="Mark completed"
        onClick={() => onSetStatus?.(job, "completed")}
        disabled={!canComplete}
      />

      <div className="my-1 border-t border-[var(--sf-border-light,#e2e8f0)]" />

      <Item
        icon={XCircle}
        label="Cancel job"
        onClick={() => onCancelJob?.(job)}
        danger
        disabled={!canCancel}
      />
    </div>
  )
}
